import * as XLSX from "xlsx";
import type { Dayjs } from "dayjs";
import {
  FilterState,
  SelectOption,
  LastImRequest,
  MarginBy,
} from "./initialMargin.types";
import axiosInstance from "../../Utils/axiosInstance";

/* ---------- constants ---------- */

// Header row of the Positions.xlsx sent to run-im (same order as XLSX_COLUMNS)
export const XLSX_HEADERS = [
  "Position Flag",
  "Portfolio Name",
  "Exchange Code",
  "Exchange Contract Code",
  "Contract Type",
  "Expiry Date",
  "Strike Price",
  "Net Position",
  "Outright Margin",
  "Regime",
  "Customer Type",
  "Strategy",
];
export const XLSX_COLUMNS = [
  "PositionFlag",
  "Account",
  "ExchangeCode",
  "ContractCode",
  "ContractType",
  "ExpiryYYYYMMDD",
  "Strike",
  "NetPosition",
  "OutrightMargin",
  "Regime",
  "CustomerType",
  "Strategy",
];

// Polling of the run-im job
export const IM_POLL_INTERVAL_MS = 3000;
// backend allows the calculator 30 min (IRM2_TIMEOUT) plus 2 min task overhead
export const IM_POLL_TIMEOUT_MS = 32 * 60 * 1000;
export const IM_JOB_DONE_STATUSES = [
  "completed",
  "complete",
  "done",
  "success",
  "succeeded",
  "finished",
];
export const IM_JOB_FAILED_STATUSES = ["failed", "failure", "error"];

// Columns of the run-im job result's buffered_margins rows
export const IM_RESULT_HEADERS: Record<string, string> = {
  portfolio: "Portfolio Name",
  strategy: "Strategy",
  im1: "IRM1",
  im2: "IRM2",
  buffer_factor: "IRM Factor",
  buffered_im: "Total IRM",
};

// Starting widths (px) of the result columns; users can still resize them
export const IM_RESULT_COLUMN_SIZES: Record<string, number> = {
  portfolio: 180,
  strategy: 100,
  buffer_factor: 110,
  im1: 130,
  im2: 130,
  buffered_im: 140,
};

export const TILL_DATE_FORMAT = "YYYY-MM-DD";
export const LAST_DATE_FORMAT = "YYYYMMDD";

// The two Run IM buttons, in display order (shared with the summary page)
export const MARGIN_BY_MODES: { value: MarginBy; label: string; hint: string }[] = [
  {
    value: "hof",
    label: "HOF",
    hint: "Each book or counterparty is margined as one portfolio, all strategies together",
  },
  {
    value: "hof_strategy",
    label: "HOF + Strategy",
    hint: "Each book and strategy is margined separately",
  },
];

// "20261026" -> "26 Oct 2026"; anything else is shown as-is
export const formatBusinessDate = (value: any): string => {
  const s = String(value ?? "");
  const m = /^(\d{4})(\d{2})(\d{2})$/.exec(s);
  if (!m) return s;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return isNaN(d.getTime())
    ? s
    : d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};

export const marginByLabel = (value: any): string =>
  MARGIN_BY_MODES.find((m) => m.value === value)?.label ?? String(value ?? "");

/* ---------- job response helpers ---------- */

// Result rows of a finished run-im job
export const getBufferedRows = (result: any): any[] =>
  Array.isArray(result?.buffered_margins) ? result.buffered_margins : [];

// Marks the rows of the HOF + Strategy view (strategy rows, the HOF row)
export const ROW_TYPE_KEY = "_rowType";
export type CombinedRowType = "strategy" | "hof";
export const HOF_STRATEGY_LABEL = "HOF";

// HOF + Strategy view, per book:
//   the HOF row exactly as the hof job returned it (strategy: null),
//   then one row per strategy (hof_strategy job) as its breakup.
// HOF is never derived from the strategy rows: margin isn't additive.
export const buildCombinedRows = (strategyRows: any[], hofRows: any[]): any[] => {
  const bookOrder: string[] = [];
  const byBook = new Map<string, any[]>();
  const addBook = (book: string) => {
    if (!byBook.has(book)) {
      byBook.set(book, []);
      bookOrder.push(book);
    }
  };

  strategyRows.forEach((r) => {
    const book = String(r?.portfolio ?? "");
    addBook(book);
    byBook.get(book)!.push(r);
  });

  // the HOF job has one row per book; prefer the strategy: null row if there are several
  const hofByBook = new Map<string, any>();
  hofRows.forEach((r) => {
    const book = String(r?.portfolio ?? "");
    if (!hofByBook.has(book) || r?.strategy == null) hofByBook.set(book, r);
    addBook(book); // books that only the HOF job returned still get their HOF row
  });

  const result: any[] = [];
  bookOrder.forEach((book) => {
    // HOF row first, then the strategy breakup just below it
    const hof = hofByBook.get(book);
    if (hof) {
      result.push({
        ...hof,
        portfolio: book,
        strategy: HOF_STRATEGY_LABEL,
        [ROW_TYPE_KEY]: "hof",
      });
    }

    const rows = byBook.get(book) ?? [];
    rows.forEach((r) => result.push({ ...r, [ROW_TYPE_KEY]: "strategy" }));
  });
  return result;
};

// Row styling of the HOF + Strategy view: the HOF row stands out and its top
// border starts each book's group
export const combinedRowSx = (rowType: CombinedRowType | undefined): any =>
  rowType === "hof"
    ? {
        "& td": {
          fontWeight: 700,
          backgroundColor: "#e6f6f9 !important",
          borderTop: "2px solid #b6dfe6 !important",
        },
      }
    : {};

/* ---------- API calls ---------- */

const EMPTY_OPTION: SelectOption = { value: "", label: "" };

// APIs return either a plain array, { data: [] } or { results: [] }
const toList = (apiData: any): any[] => {
  if (Array.isArray(apiData)) return apiData;
  if (Array.isArray(apiData?.data)) return apiData.data;
  if (Array.isArray(apiData?.results)) return apiData.results;
  return [];
};

/* ---------- dropdown options ---------- */

export const fetchBookOptions = async (): Promise<SelectOption[]> => {
  const { data } = await axiosInstance.get("/paper-app/books/");
  return [
    EMPTY_OPTION,
    ...toList(data).map((b) => ({
      value: String(b.name),
      label: b.name,
      id: String(b.id),
    })),
  ];
};

export const fetchCounterpartyOptions = async (): Promise<SelectOption[]> => {
  const { data } = await axiosInstance.get(
    "/paper-app/counterparty-companies/"
  );
  return [
    EMPTY_OPTION,
    ...toList(data).map((c) => ({
      value: c.customer_company_name,
      label: c.customer_company_name,
      id: String(c.id),
    })),
  ];
};

export const fetchClearerOptions = async (): Promise<SelectOption[]> => {
  const { data } = await axiosInstance.get("/paper-app/clearers/");
  return [
    EMPTY_OPTION,
    ...toList(data).map((c) => ({
      value: String(c.name || c.id),
      label: c.name || c.id,
      id: String(c.id),
    })),
  ];
};

export const fetchStrategyOptions = async (): Promise<SelectOption[]> => {
  const { data } = await axiosInstance.get("/paper-app/strategies/");
  return [
    EMPTY_OPTION,
    ...toList(data).map((s) => ({
      value: String(s.name),
      label: s.name || s.id,
      id: String(s.id),
    })),
  ];
};

/* ---------- positions ---------- */

// Returns null when backend says there are no trades
export const fetchPositions = async (
  filters: FilterState,
  tillDate: Dayjs | null,
  includeTillDate: boolean
): Promise<any[] | null> => {
  const params: Record<string, string> = {};
  Object.entries(filters).forEach(([key, value]) => {
    const paramValue = Array.isArray(value) ? value.join(",") : value;
    if (paramValue) params[key] = paramValue;
  });
  if (tillDate && tillDate.isValid()) {
    params.till_date = tillDate.format(TILL_DATE_FORMAT);
  }
  params.include_till_date = String(includeTillDate);

  const { data } = await axiosInstance.get(
    "/paper-app/intial-margin-position/",
    { params }
  );

  if (
    data?.message === "No trades found for either futures or swaps." ||
    (typeof data === "string" &&
      data.includes("No trades found for either futures or swaps"))
  ) {
    return null;
  }

  return data.map((item: any) => ({
    PositionFlag: item["Position Flag"] || "P",
    Account: item["Account"] || "",
    ExchangeCode: item["Exchange Code"] || "",
    ContractCode: item["Contract Code"] || "",
    ContractType: item["Contract Type"] || "F",
    ExpiryYYYYMMDD: item["Expiry"] || "",
    Strike: item["Strike Price"] ?? "",
    NetPosition: item["Net Position"] || 0,
    Regime: item["Regime"] || "",
    CustomerType: item["Customer Type"] || "",
    OutrightMargin: item["Outright Margin"] ?? "",
    Strategy: item["Strategy"] ?? "",
  }));
};

/* ---------- run-im ---------- */

export const buildPositionsBlob = (rows: any[]): Blob => {
  const sheetData = [
    XLSX_HEADERS,
    ...rows.map((r) => XLSX_COLUMNS.map((col) => r[col] ?? "")),
  ];
  const worksheet = XLSX.utils.aoa_to_sheet(sheetData);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Positions");
  const buffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
  return new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
};

// rerun=true asks the backend to recalculate instead of returning a reused result
export const startImJob = async (
  req: LastImRequest,
  rerun = false
): Promise<{ jobId: string | number; reused: boolean }> => {
  const body = new FormData();
  body.append("file", req.file, "Positions.xlsx");
  body.append("business_date", req.businessDate);
  if (req.tillDate) body.append("till_date", req.tillDate);
  body.append("include_till_date", String(req.includeTillDate));
  if (req.companyType) body.append("company_type", req.companyType);
  if (rerun) body.append("rerun", "true");

  const res = await axiosInstance.post("/paper-app/run-im/", body, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  const jobId = res.data?.job_id ?? res.data?.id;
  if (!jobId) {
    throw new Error(res.data?.error || "No job id returned from server");
  }
  return { jobId, reused: res.data?.reused === true };
};

// Polls until done. Returns null if the page was left (isMounted() false).
export const pollImJob = async (
  jobId: string | number,
  isMounted: () => boolean
): Promise<any | null> => {
  const startedAt = Date.now();
  let lastStatus = "";
  while (true) {
    await new Promise((r) => setTimeout(r, IM_POLL_INTERVAL_MS));
    if (!isMounted()) return null;

    const { data: job } = await axiosInstance.get(
      `/paper-app/margin/jobs-org/${jobId}/`
    );
    if (!isMounted()) return null;

    const status = String(job?.status ?? job?.state ?? "")
      .trim()
      .toLowerCase();
    lastStatus = status;
    if (IM_JOB_DONE_STATUSES.includes(status)) return job;
    if (IM_JOB_FAILED_STATUSES.includes(status)) {
      throw new Error(job?.error || job?.message || "IM calculation failed");
    }
    if (Date.now() - startedAt > IM_POLL_TIMEOUT_MS) {
      throw new Error(
        lastStatus === "queued"
          ? "IM calculation never started (job still queued). The margin worker may not be running."
          : `IM calculation timed out (last status: ${
              lastStatus || "unknown"
            }). Please try again.`
      );
    }
  }
};